import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

interface ContributionFormProps {
  officialId: string;
  officialName: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const contributionTypes = [
  { value: 'accountability_record', label: 'Accountability Record', description: 'Voting record, statement, or action' },
  { value: 'document', label: 'Official Document', description: 'Government filing, report, or disclosure' },
  { value: 'news_link', label: 'News Article', description: 'Media coverage or investigative report' },
  { value: 'social_post', label: 'Social Media Post', description: 'Official statement on social media' },
  { value: 'other', label: 'Other', description: 'Other relevant information' },
];

export default function ContributionForm({ officialId, officialName, onSuccess, onCancel }: ContributionFormProps) {
  const { token, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    contribution_type: 'accountability_record',
    title: '',
    description: '',
    source_url: '',
    evidence_urls: '',
    submission_date: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!isAuthenticated) {
      setError('You must be logged in to submit a contribution');
      return;
    }

    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Title and description are required');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/contributions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          official_id: officialId,
          ...formData,
          submission_date: formData.submission_date ? new Date(formData.submission_date).getTime() : Date.now(),
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit contribution');
      }

      setSuccess(true);
      setFormData({
        contribution_type: 'accountability_record',
        title: '',
        description: '',
        source_url: '',
        evidence_urls: '',
        submission_date: '',
      });

      if (onSuccess) {
        setTimeout(() => onSuccess(), 1500);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit contribution');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl p-8 text-center">
        <svg className="w-12 h-12 mx-auto mb-4 text-navy-400 dark:text-navy-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <p className="text-sm font-bold text-navy-700 dark:text-cream-200 mb-2">Login Required</p>
        <p className="text-xs text-navy-500 dark:text-navy-300 mb-4">You must be logged in to submit contributions</p>
        <a href="/login" className="inline-block px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold uppercase tracking-wider rounded transition">
          Login
        </a>
      </div>
    );
  }

  if (success) {
    return (
      <div className="bg-white dark:bg-navy-800/40 border border-emerald-500/30 rounded-xl p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 flex items-center justify-center">
          <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-black text-navy-900 dark:text-cream-50 mb-2">Contribution Submitted!</h3>
        <p className="text-sm text-navy-600 dark:text-cream-200 mb-4">
          Thank you for your contribution. It will be reviewed by our moderators before being published.
        </p>
        <p className="text-xs text-navy-500 dark:text-navy-300">
          Community contributions help keep officials accountable.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-cream-300 dark:border-navy-600 bg-cream-50 dark:bg-navy-800/20">
        <h3 className="text-sm font-bold text-navy-900 dark:text-cream-50">Submit Contribution</h3>
        <p className="text-xs text-navy-600 dark:text-cream-300 mt-1">Add accountability information for {officialName}</p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-5">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Contribution Type */}
        <div>
          <label className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
            Contribution Type *
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {contributionTypes.map((type) => (
              <label
                key={type.value}
                className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition ${
                  formData.contribution_type === type.value
                    ? 'border-blue-500 bg-blue-500/5'
                    : 'border-cream-300 dark:border-navy-600 hover:border-blue-400 dark:hover:border-blue-500'
                }`}
              >
                <input
                  type="radio"
                  name="contribution_type"
                  value={type.value}
                  checked={formData.contribution_type === type.value}
                  onChange={(e) => setFormData({ ...formData, contribution_type: e.target.value })}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-xs font-bold text-navy-900 dark:text-cream-50">{type.label}</p>
                  <p className="text-[10px] text-navy-500 dark:text-navy-300 mt-0.5">{type.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
            Title *
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Brief, descriptive title"
            className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
            Description *
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Detailed description of the accountability issue or information"
            rows={5}
            className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50 resize-y"
            required
          />
          <p className="text-[9px] text-navy-500 dark:text-navy-300 mt-1">
            Provide context, facts, and why this matters for accountability
          </p>
        </div>

        {/* Source URL */}
        <div>
          <label htmlFor="source_url" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
            Primary Source URL
          </label>
          <input
            type="url"
            id="source_url"
            value={formData.source_url}
            onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
            placeholder="https://example.com/article"
            className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
          />
        </div>

        {/* Evidence URLs */}
        <div>
          <label htmlFor="evidence_urls" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
            Additional Evidence URLs
          </label>
          <textarea
            id="evidence_urls"
            value={formData.evidence_urls}
            onChange={(e) => setFormData({ ...formData, evidence_urls: e.target.value })}
            placeholder="https://example.com/doc1&#10;https://example.com/doc2"
            rows={3}
            className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50 resize-y"
          />
          <p className="text-[9px] text-navy-500 dark:text-navy-300 mt-1">
            One URL per line (optional)
          </p>
        </div>

        {/* Submission Date */}
        <div>
          <label htmlFor="submission_date" className="block text-[10px] font-bold text-navy-700 dark:text-cream-200 uppercase tracking-wider mb-2">
            Event/Publication Date
          </label>
          <input
            type="date"
            id="submission_date"
            value={formData.submission_date}
            onChange={(e) => setFormData({ ...formData, submission_date: e.target.value })}
            className="w-full px-4 py-2.5 text-sm bg-white dark:bg-navy-900 border border-cream-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-navy-900 dark:text-cream-50"
          />
          <p className="text-[9px] text-navy-500 dark:text-navy-300 mt-1">
            When did this event occur or article publish?
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-4 border-t border-cream-300 dark:border-navy-600">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-navy-600 dark:text-cream-300 hover:text-navy-900 dark:hover:text-cream-50 transition"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-5 py-2.5 text-xs font-bold uppercase tracking-wider bg-blue-600 hover:bg-blue-700 disabled:bg-navy-400 text-white rounded-lg transition"
          >
            {loading ? 'Submitting...' : 'Submit Contribution'}
          </button>
        </div>

        <p className="text-[9px] text-navy-500 dark:text-navy-300 text-center pt-2">
          All contributions are reviewed by moderators before being published. Please ensure accuracy and provide credible sources.
        </p>
      </form>
    </div>
  );
}
